import { TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { computePageBuckets } from "./public-flow-utils";
import { PublicPageRenderer, type PublicPageRendererContext } from "./public-page-renderer";

type GlobalPagesFlowBodyProps = {
  allPages: any[];
  ctx: PublicPageRendererContext;
};

/**
 * QL-682 — public flow body for a flow whose public pages are all global
 * (server flag `hasGoalPages` is false). One row of global-page tabs and the
 * selected page — no goal tabs, no Add Goal, no portfolio dimension: with no
 * goal or portfolio page to fill in, those would only lead to empty tabs.
 *
 * Global components still get the instance's first goal, as in the goal
 * bodies' `renderGlobalPage`.
 */
export function GlobalPagesFlowBody({ allPages, ctx }: GlobalPagesFlowBodyProps) {
  const pages = useMemo(() => {
    const buckets = computePageBuckets(allPages, ctx.formValues, ctx.goals, ctx.fieldTypes);
    return [...buckets.globalPagesBefore, ...buckets.globalPagesAfter];
  }, [allPages, ctx.formValues, ctx.goals, ctx.fieldTypes]);

  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  // Falls back to the first page when the selected one is hidden by its rule.
  const selectedPage = pages.find((p) => p.id === selectedPageId) ?? pages[0];
  if (!selectedPage) return null;

  return (
    <div className="space-y-4">
      <Tabs value={selectedPage.id} onValueChange={setSelectedPageId}>
        <TabsList className="flex w-fit max-w-full justify-start gap-1">
          {pages.map((page) => (
            <TabsTrigger
              key={page.id}
              value={page.id}
              className="shrink-0 flex items-center justify-center gap-1.5"
            >
              {page.name}
              {ctx.showValidationErrors && ctx.markers.missingPageIds.has(page.id) && (
                <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <PublicPageRenderer page={selectedPage} goalId={ctx.goals[0]?.id ?? null} ctx={ctx} />
    </div>
  );
}
